import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import WeekPicker from '../../standouts/components/WeekPicker';
import OrgSearchDropdown from '../../standouts/components/OrgSearchDropdown';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import AthletePicker from '../components/AthletePicker';
import { useAuth } from '../../contexts/AuthContext';
import { uploadImageToCloudflare } from '../../utils/cloudflareUpload';
import {
  SPORTS, AWARDS, DIVISIONS, LEVELS_GRADE, LEVELS_AGE, LEVELS_SCHOOL,
  SEASONS, GENDERS, STATUS_LABELS,
} from '../../standouts/data/standoutsConstants';
import '../styles/StandoutsScreen.css';
import '../../standouts/styles/SubmitForm.css';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}

const STATUS_TABS = [
  { id: 'all',      label: 'All' },
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
];

const EMPTY_FORM = {
  status: 'published',
  submitter_name: '', submitter_email: '',
  org_name: '', org_short_name: '', org_location: '',
  athlete_name: '', athlete_user_id: '', athlete_user_name: '', team_name: '', sport: 'Flag Football',
  level: '', division: '', gender: '',
  award: 'MVP', stats: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }],
  photo_url: '',
  coach_name: '', coach_title: '', coach_note: '',
  game_opponent: '', game_date: '', game_result: '', game_location: '',
  week_number: '', week_start_date: '',
  week_label: '', week_date_range: '', season_name: 'Spring', season_year: 2026,
  featured: false,
};

const StandoutsScreen = () => {
  const {
    getStandouts, createStandoutDirect, updateStandout, toggleStandoutFeatured,
    updateStandoutStatus, bulkUpdateStandoutStatus, deleteStandout, getAthletesByIds,
  } = useAuth();
  const [activeItem, setActiveItem] = useState('standouts');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [linkedMap, setLinkedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  const [selectedIds, setSelectedIds] = useState([]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);


  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await getStandouts();
    if (result.success) setData(result.data || []);
    else showNotification('error', result.error || 'Failed to load standouts');
    setLoading(false);
  }, [getStandouts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Resolve linked athlete profiles for the list.
  useEffect(() => {
    const ids = [...new Set(data.map((s) => s.athlete_user_id).filter(Boolean))];
    if (ids.length === 0) { setLinkedMap({}); return; }
    getAthletesByIds(ids).then((res) => {
      if (!res.success) return;
      const map = {};
      res.data.forEach((a) => { map[a.user_id] = a; });
      setLinkedMap(map);
    });
  }, [data, getAthletesByIds]);

  const filtered = data.filter((s) => {
    const tabMatch = activeTab === 'all' || s.status === activeTab;
    const searchMatch = !search ||
      (s.athlete_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.team_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.org_name || '').toLowerCase().includes(search.toLowerCase());
    return tabMatch && searchMatch;
  });

  const counts = {
    all: data.length,
    pending: data.filter((s) => s.status === 'pending').length,
    approved: data.filter((s) => s.status === 'approved').length,
    published: data.filter((s) => s.status === 'published').length,
    rejected: data.filter((s) => s.status === 'rejected').length,
  };

  // ── Selection ──
  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map((s) => s.id));
  };
  const clearSelection = () => setSelectedIds([]);

  // ── Single status actions ──
  const handleStatus = async (id, status) => {
    if (status === 'approved') {
      const standout = data.find((s) => s.id === id);
      if (!standout?.week_label) {
        showNotification('error', 'Week number is required before approving. Please edit this standout first.');
        return;
      }
    }
    const result = await updateStandoutStatus(id, status);
    if (result.success) {
      await fetchData();
      showNotification('success', `Standout ${status}.`);
    } else {
      showNotification('error', result.error || 'Failed to update status');
    }
  };

  // ── Bulk actions ──
  const handleBulkStatus = async (status) => {
    if (selectedIds.length === 0) return;
    const result = await bulkUpdateStandoutStatus(selectedIds, status);
    if (result.success) {
      await fetchData();
      clearSelection();
      showNotification('success', `${selectedIds.length} standouts ${status}.`);
    } else {
      showNotification('error', result.error || 'Bulk update failed');
    }
  };

  // ── Instant Publish ──
  const handleInstantPublish = async (id) => {
    const standout = data.find((s) => s.id === id);
    if (!standout?.week_label) {
      showNotification('error', 'Week number is required before publishing. Please edit this standout first.');
      return;
    }
    if (!window.confirm('Publish this standout immediately?')) return;
    const result = await updateStandoutStatus(id, 'published');
    if (result.success) {
      await fetchData();
      showNotification('success', 'Standout published instantly.');
    } else {
      showNotification('error', result.error || 'Failed to publish');
    }
  };

  // ── Publishes In countdown ──
  const getPublishesIn = () => {
    const now = new Date();
    const day = now.getDay();
    const next = new Date(now);
    if (day === 1 && now.getHours() < 5) {
      next.setHours(5, 0, 0, 0);
    } else {
      const daysUntil = day === 1 ? 7 : (8 - day) % 7;
      next.setDate(now.getDate() + daysUntil);
      next.setHours(5, 0, 0, 0);
    }
    const diff = next - now;
    if (diff <= 0) return 'Publishing soon';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days === 0) return `${hours} hrs`;
    return `${days}d ${hours}h`;
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this standout permanently?')) return;
    const result = await deleteStandout(id);
    if (result.success) {
      await fetchData();
      showNotification('success', 'Standout deleted.');
    } else {
      showNotification('error', result.error || 'Delete failed');
    }
  };

  // ── Toggle featured ──
  const handleToggleFeatured = async (s) => {
    const result = await toggleStandoutFeatured(s.id, s.featured);
    if (result.success) {
      await fetchData();
      showNotification('success', `${s.athlete_name} ${!s.featured ? 'pinned to homepage.' : 'unpinned from homepage.'}`);
    } else {
      showNotification('error', result.error || 'Failed to update');
    }
  };

  // ── Create / Edit modal ──
  const openCreateModal = () => {
    setEditingId(null);
    setSelectedOrg(null);
    setForm({
      ...EMPTY_FORM,
      stats: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }],
    });
    setShowFormModal(true);
  };

  const openEditModal = (s) => {
    setEditingId(s.id);
    const stats = Array.isArray(s.stats) ? s.stats : [];
    const padded = [...stats];
    while (padded.length < 3) padded.push({ value: '', label: '' });
    setSelectedOrg(s.org_name ? { name: s.org_name, slug: s.org_slug, city: s.org_location, sport: s.sport } : null);
    setForm({
      status: s.status || 'approved',
      submitter_name: s.submitter_name || '', submitter_email: s.submitter_email || '',
      org_name: s.org_name || '', org_short_name: s.org_short_name || '', org_location: s.org_location || '',
      athlete_name: s.athlete_name || '', athlete_user_id: s.athlete_user_id || '', athlete_user_name: s.athlete_user_id ? (s.athlete_name || '') : '',
      team_name: s.team_name || '', sport: s.sport || 'Flag Football',
      level: s.level || '', division: s.division || '', gender: s.gender || '',
      award: s.award || 'MVP', stats: padded.slice(0, 3),
      photo_url: s.photo_url || '',
      coach_name: s.coach_name || '', coach_title: s.coach_title || '', coach_note: s.coach_note || '',
      game_opponent: s.game_details?.opponent || '',
      game_date: s.game_details?.date || '',
      game_result: s.game_details?.result || '',
      game_location: s.game_details?.location || '',
      week_number: s.week_label ? s.week_label.replace(/[^0-9]/g, '') : '',
      week_start_date: '',
      week_label: s.week_label || '', week_date_range: s.week_date_range || '',
      season_name: (s.season || 'Spring 2026').split(' ')[0] || 'Spring',
      season_year: parseInt((s.season || 'Spring 2026').split(' ')[1]) || 2026,
      featured: s.featured || false,
    });
    setShowFormModal(true);
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateStat = (i, key, value) => {
    setForm((prev) => {
      const next = [...prev.stats];
      next[i] = { ...next[i], [key]: value };
      return { ...prev, stats: next };
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropDone = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);
    setCropModalOpen(false);
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const croppedFile = new File([blob], 'athlete.jpg', { type: 'image/jpeg' });
      const url = await uploadImageToCloudflare(croppedFile);
      updateField('photo_url', url);
    } catch (err) {
      showNotification('error', 'Photo upload failed');
    } finally {
      setUploading(false);
      setRawImageSrc(null);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const handleSave = async () => {
    if (!selectedOrg || !form.athlete_name.trim() || !form.sport.trim()) {
      showNotification('error', 'Organization, Athlete Name and Sport are required');
      return;
    }
    setSaving(true);
    const cleanStats = form.stats.filter((s) => (s.value || '').toString().trim() && (s.label || '').toString().trim());
    const payload = {
      status: form.status,
      submitter_name: form.submitter_name, submitter_email: form.submitter_email,
      org_name: selectedOrg.name,
      org_short_name: selectedOrg.short_name || selectedOrg.name,
      org_location: selectedOrg.city && selectedOrg.state ? `${selectedOrg.city}, ${selectedOrg.state}` : (selectedOrg.city || selectedOrg.state || selectedOrg.org_location || ''),
      athlete_name: form.athlete_name, athlete_user_id: form.athlete_user_id || null,
      team_name: form.team_name, sport: form.sport,
      level: form.level, division: form.division, gender: form.gender,
      award: form.award, stats: cleanStats, photo_url: form.photo_url,
      coach_name: form.coach_name, coach_title: form.coach_title, coach_note: form.coach_note,
      game_details: {
        opponent: form.game_opponent, date: form.game_date,
        result: form.game_result, location: form.game_location,
      },
      week_label: form.week_number ? `Week ${form.week_number}` : '',
      week_date_range: form.week_date_range,
      season: `${form.season_name} ${form.season_year}`,
      featured: form.featured,
    };

    const result = editingId
      ? await updateStandout(editingId, payload)
      : await createStandoutDirect(payload);

    setSaving(false);
    if (result.success) {
      setShowFormModal(false);
      await fetchData();
      showNotification('success', editingId ? 'Standout updated.' : 'Standout created.');
    } else {
      showNotification('error', result.error || 'Save failed');
    }
  };

  const allSelected = filtered.length > 0 && selectedIds.length === filtered.length;

  return (
    <div className="dashboard-layout">
      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />

      <div className="dashboard-main">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Standouts"
          subtitle="Review and manage weekly athlete standout submissions"
        />

        <div className="standouts-screen">


          {notification.show && (
            <div className={`ss-notification ss-notification-${notification.type}`}>
              {notification.message}
            </div>
          )}

          {/* Stats */}
          <div className="ss-stats-row">
            {STATUS_TABS.map((t) => (
              <div key={t.id} className={`ss-stat-card ${activeTab === t.id ? 'active' : ''}`} onClick={() => { setActiveTab(t.id); clearSelection(); }}>
                <span className="ss-stat-count">{counts[t.id]}</span>
                <span className="ss-stat-label">{t.label}</span>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="ss-controls">
            <input
              type="text"
              className="ss-search"
              placeholder="Search athlete, team or organization..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="ss-create-btn" onClick={openCreateModal}>
              + Create Standout
            </button>
            <button
              className="ss-view-live-btn"
              onClick={() => window.open('/standouts', '_blank')}
            >
              View Live Page ↗
            </button>
          </div>

          {/* Tabs */}
          <div className="ss-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                className={`ss-tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => { setActiveTab(t.id); clearSelection(); }}
              >
                {t.label}
                <span className="ss-tab-count">{counts[t.id]}</span>
              </button>
            ))}
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.length > 0 && (
            <div className="ss-bulk-bar">
              <span className="ss-bulk-count">{selectedIds.length} selected</span>
              <div className="ss-bulk-actions">
                <button className="ss-bulk-btn approve" onClick={() => handleBulkStatus('approved')}>Approve</button>
                <button className="ss-bulk-btn reject" onClick={() => handleBulkStatus('rejected')}>Reject</button>
                <button className="ss-bulk-btn cancel" onClick={clearSelection}>Cancel</button>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="ss-empty">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="ss-empty">No submissions match your filters.</div>
          ) : (
            <div className="ss-table-wrap">
              <table className="ss-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    </th>
                    <th>Athlete</th>
                    <th>Linked Athlete</th>
                    <th>Award</th>
                    <th>Org / Division</th>
                    <th>Week</th>
                    <th>Status</th>
                    <th>Publishes In</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                    const rows = [];
                    let lastDay = null;
                    filtered.forEach((s) => {
                      const d = new Date(s.created_at);
                      const dayLabel = `${DAYS[d.getDay()]} · ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                      if (dayLabel !== lastDay) {
                        lastDay = dayLabel;
                        rows.push(
                          <tr key={`day-${dayLabel}`} className="ss-day-row">
                            <td colSpan={9}>{dayLabel}</td>
                          </tr>
                        );
                      }
                      rows.push(
                        <tr key={s.id} className={selectedIds.includes(s.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(s.id)}
                          onChange={() => toggleSelect(s.id)}
                        />
                      </td>
                      <td>
                        <div className="ss-athlete-cell">
                          <img
                            src={s.photo_url || '/assets/images/athlete-placeholder.png'}
                            alt={s.athlete_name}
                            className="ss-athlete-thumb"
                          />
                          <div>
                            <strong className="ss-athlete-name">{s.athlete_name}</strong>
                            <span className="ss-team-name">{s.team_name || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {s.athlete_user_id ? (
                          (() => {
                            const la = linkedMap[s.athlete_user_id];
                            return (
                              <div className="ss-linked-cell">
                                {la?.profile_image ? (
                                  <img src={la.profile_image} alt="" className="ss-linked-thumb" />
                                ) : (
                                  <span className="ss-linked-thumb ss-linked-thumb--initial">
                                    {(la?.full_name || '?').charAt(0).toUpperCase()}
                                  </span>
                                )}
                                <div className="ss-linked-info">
                                  <strong className="ss-linked-name">{la?.full_name || 'Linked athlete'}</strong>
                                  <span className="ss-linked-email">{la?.email || '—'}</span>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="ss-linked-none">Not linked</span>
                        )}
                      </td>
                      <td>
                        <span className="ss-award-badge" style={{ background: 'rgba(216,161,52,0.13)', color: '#D8A134', borderColor: 'rgba(216,161,52,0.28)' }}>
                          {s.award || '—'}
                        </span>
                      </td>
                      <td>
                        <span className="ss-league">{s.org_short_name || s.org_name || '—'}</span>
                        <span className="ss-division">{[s.division, s.level].filter(Boolean).join(' · ') || '—'}</span>
                      </td>
                      <td className="ss-week">{s.week_label || '—'}</td>
                      <td>
                        <span className={`ss-status-pill ss-status-${s.status}`}>
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                      </td>
                      <td>
                        {s.status === 'approved' ? (
                          <span className="ss-publishes-in">{getPublishesIn()}</span>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="ss-actions">
                          <button
                            className={`ss-action-btn featured${s.featured ? ' active' : ''}`}
                            onClick={() => handleToggleFeatured(s)}
                            title={s.featured ? 'Unpin from homepage' : 'Pin to homepage'}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={s.featured ? '#D8A134' : 'none'} stroke={s.featured ? '#D8A134' : 'currentColor'} strokeWidth="2">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                            </svg>
                          </button>
                          <button
                            className="ss-action-btn view"
                            onClick={() => window.open(`/standouts/athlete/${s.id}`, '_blank')}
                            title="View"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          <button
                            className="ss-action-btn edit"
                            onClick={() => openEditModal(s)}
                            title="Edit"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          {s.status !== 'approved' && s.status !== 'published' && (
                            <button
                              className="ss-action-btn approve"
                              onClick={() => handleStatus(s.id, 'approved')}
                              title="Approve"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            </button>
                          )}
                          {s.status !== 'published' && (
                            <button
                              className="ss-action-btn publish"
                              onClick={() => handleInstantPublish(s.id)}
                              title="Instant Publish"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                              </svg>
                            </button>
                          )}
                          {s.status !== 'rejected' && (
                            <button
                              className="ss-action-btn reject"
                              onClick={() => handleStatus(s.id, 'rejected')}
                              title="Reject"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className="ss-action-btn delete"
                            onClick={() => handleDelete(s.id)}
                            title="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                      );
                    });
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ───────────── Create/Edit Modal ───────────── */}
      {showFormModal && (
        <div className="ss-modal-overlay" onClick={() => !saving && setShowFormModal(false)}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ss-modal-header">
              <h2>{editingId ? 'Edit Standout' : 'Create Standout'}</h2>
              <button className="ss-modal-close" onClick={() => setShowFormModal(false)}>×</button>
            </div>

            <div className="ss-modal-body">

              {/* Status */}
              <div className="ss-modal-section">
                <h3>Status</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Status</span>
                    <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                      {Object.entries(STATUS_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="ss-form-row" style={{ marginTop: '12px' }}>
                  <label className="ss-featured-toggle" onClick={() => updateField('featured', !form.featured)}>
                    <div className={`ss-toggle-switch${form.featured ? ' on' : ''}`}>
                      <div className="ss-toggle-knob" />
                    </div>
                    <div>
                      <span className="ss-toggle-label">Featured on Homepage</span>
                      <span className="ss-toggle-sub">Featured cards appear first on the Standouts page</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Organization */}
              <div className="ss-modal-section">
                <h3>Organization</h3>
                <label className="ss-form-field">
                  <span>Select Organization *</span>
                  <OrgSearchDropdown
                    value={selectedOrg}
                    onChange={(org) => setSelectedOrg(org)}
                    placeholder="Search organization..."
                  />
                </label>
              </div>

              {/* Submitter */}
              <div className="ss-modal-section">
                <h3>Submitter (optional)</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Submitter Name</span>
                    <input value={form.submitter_name} onChange={(e) => updateField('submitter_name', e.target.value)} />
                  </label>
                  <label className="ss-form-field">
                    <span>Submitter Email</span>
                    <input value={form.submitter_email} onChange={(e) => updateField('submitter_email', e.target.value)} type="email" />
                  </label>
                </div>
              </div>

              {/* Athlete */}
              <div className="ss-modal-section">
                <h3>Athlete</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field" style={{ flex: 1 }}>
                    <span>Link to Registered Athlete</span>
                    <AthletePicker
                      value={form.athlete_user_id || null}
                      linkedName={form.athlete_user_name}
                      onChange={(userId, athlete) => {
                        updateField('athlete_user_id', userId || '');
                        updateField('athlete_user_name', athlete?.full_name || '');
                        // Prefill the display name from the linked profile when empty.
                        if (athlete?.full_name && !form.athlete_name.trim()) {
                          updateField('athlete_name', athlete.full_name);
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Athlete Name *</span>
                    <input value={form.athlete_name} onChange={(e) => updateField('athlete_name', e.target.value)} />
                  </label>
                  <label className="ss-form-field">
                    <span>Team Name</span>
                    <input value={form.team_name} onChange={(e) => updateField('team_name', e.target.value)} />
                  </label>
                </div>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Sport *</span>
                    <select value={form.sport} onChange={(e) => updateField('sport', e.target.value)}>
                      {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="ss-form-field">
                    <span>Division</span>
                    <select value={form.division} onChange={(e) => updateField('division', e.target.value)}>
                      <option value="">Select</option>
                      {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>
                  <label className="ss-form-field">
                    <span>Level</span>
                    <select value={form.level} onChange={(e) => updateField('level', e.target.value)}>
                      <option value="">Select</option>
                      <optgroup label="Grade Levels">
                        {LEVELS_GRADE.map((l) => <option key={l} value={l}>{l}</option>)}
                      </optgroup>
                      <optgroup label="Age Levels">
                        {LEVELS_AGE.map((l) => <option key={l} value={l}>{l}</option>)}
                      </optgroup>
                      <optgroup label="School Levels">
                        {LEVELS_SCHOOL.map((l) => <option key={l} value={l}>{l}</option>)}
                      </optgroup>
                    </select>
                  </label>
                  <label className="ss-form-field">
                    <span>Gender</span>
                    <select value={form.gender} onChange={(e) => updateField('gender', e.target.value)}>
                      <option value="">Select</option>
                      {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              {/* Award & Stats */}
              <div className="ss-modal-section">
                <h3>Award & Stats</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Award *</span>
                    <select value={form.award} onChange={(e) => updateField('award', e.target.value)}>
                      {AWARDS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </label>
                </div>
                {form.stats.map((stat, i) => (
                  <div className="ss-form-row" key={i}>
                    <label className="ss-form-field">
                      <span>Stat {i + 1} Value</span>
                      <input value={stat.value} onChange={(e) => updateStat(i, 'value', e.target.value)} placeholder="5" />
                    </label>
                    <label className="ss-form-field">
                      <span>Stat {i + 1} Label</span>
                      <input value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} placeholder="TDs" />
                    </label>
                  </div>
                ))}
              </div>

              {/* Photo */}
              <div className="ss-modal-section">
                <h3>Athlete Photo</h3>
                <div className="ss-photo-row">
                  {form.photo_url && (
                    <img src={form.photo_url} alt="" className="ss-photo-preview" />
                  )}
                  <label className="ss-upload-btn">
                    <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                    {uploading ? 'Uploading...' : (form.photo_url ? 'Change Photo' : 'Upload Photo')}
                  </label>
                </div>
              </div>

              {/* Coach */}
              <div className="ss-modal-section">
                <h3>Coach (optional)</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Coach Name</span>
                    <input value={form.coach_name} onChange={(e) => updateField('coach_name', e.target.value)} />
                  </label>
                  <label className="ss-form-field">
                    <span>Coach Title</span>
                    <input value={form.coach_title} onChange={(e) => updateField('coach_title', e.target.value)} placeholder="Head Coach" />
                  </label>
                </div>
                <label className="ss-form-field">
                  <span>Coach Note</span>
                  <textarea value={form.coach_note} onChange={(e) => updateField('coach_note', e.target.value)} rows={3} />
                </label>
              </div>

              {/* Game */}
              <div className="ss-modal-section">
                <h3>Game Details (optional)</h3>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Opponent</span>
                    <input value={form.game_opponent} onChange={(e) => updateField('game_opponent', e.target.value)} />
                  </label>
                  <label className="ss-form-field">
                    <span>Date</span>
                    <input value={form.game_date} onChange={(e) => updateField('game_date', e.target.value)} placeholder="Apr 22, 2026" />
                  </label>
                </div>
                <div className="ss-form-row">
                  <label className="ss-form-field">
                    <span>Result</span>
                    <input value={form.game_result} onChange={(e) => updateField('game_result', e.target.value)} placeholder="W 28-14" />
                  </label>
                  <label className="ss-form-field">
                    <span>Game Location</span>
                    <input value={form.game_location} onChange={(e) => updateField('game_location', e.target.value)} />
                  </label>
                </div>
              </div>

              {/* Week */}
              <div className="ss-modal-section">
                <h3>Week / Season</h3>
                <WeekPicker
                  week={form.week_number}
                  startDate={form.week_start_date}
                  dateRange={form.week_date_range}
                  onChange={({ week, dateRange, startDate }) => {
                    updateField('week_number', week);
                    updateField('week_date_range', dateRange);
                    updateField('week_start_date', startDate);
                  }}
                />
                <div className="ss-form-row" style={{ marginTop: '16px' }}>
                  <label className="ss-form-field">
                    <span>Season</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select value={form.season_name} onChange={(e) => updateField('season_name', e.target.value)} style={{ flex: 1 }}>
                        {SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div className="week-picker-number-wrap" style={{ width: '90px' }}>
                        <input
                          type="number"
                          value={form.season_year}
                          onChange={(e) => updateField('season_year', parseInt(e.target.value) || 2026)}
                          min="2020"
                          max="2099"
                          className="week-picker-input week-picker-number"
                        />
                        <div className="week-picker-spinners">
                          <button type="button" className="week-picker-spin" onClick={() => updateField('season_year', Math.min(2099, (form.season_year || 2026) + 1))}>
                            <img src="/assets/images/arrow-up-icon.png" alt="up" className="week-picker-spin-icon" />
                          </button>
                          <button type="button" className="week-picker-spin" onClick={() => updateField('season_year', Math.max(2020, (form.season_year || 2026) - 1))}>
                            <img src="/assets/images/arrow-down-icon.png" alt="down" className="week-picker-spin-icon" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

            </div>

            <div className="ss-modal-footer">
              <button className="ss-modal-cancel" onClick={() => setShowFormModal(false)} disabled={saving}>Cancel</button>
              <button className="ss-modal-save" onClick={handleSave} disabled={saving || uploading}>
                {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Create Standout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {cropModalOpen && (
        <div className="crop-modal-overlay">
          <div className="crop-modal">
            <h3 className="crop-modal-title">Crop Photo</h3>
            <p className="crop-modal-desc">Drag to reposition • Pinch or scroll to zoom • 4:5 ratio</p>
            <div className="crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 5}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="crop-zoom-row">
              <span className="crop-zoom-label">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="crop-zoom-slider"
              />
            </div>
            <div className="crop-modal-actions">
              <button className="crop-cancel-btn" onClick={handleCropCancel}>Cancel</button>
              <button className="crop-done-btn" onClick={handleCropDone}>Crop &amp; Upload</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StandoutsScreen;
